import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Edit,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  Columns,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'
import { itemService } from '@/services/itemService'
import type { ItemEntity, ItemLookupOption } from '@/api/types'

const PAGE_SIZE = 10

type ColumnKey = 'code' | 'barcode' | 'name' | 'description' | 'type' | 'category' | 'subCategory'

const ALL_COLUMNS: { key: ColumnKey; labelKey: string }[] = [
  { key: 'code', labelKey: 'inventoryItems.columns.code' },
  { key: 'barcode', labelKey: 'inventoryItems.columns.barcode' },
  { key: 'name', labelKey: 'inventoryItems.columns.name' },
  { key: 'description', labelKey: 'inventoryItems.columns.description' },
  { key: 'type', labelKey: 'inventoryItems.columns.type' },
  { key: 'category', labelKey: 'inventoryItems.columns.category' },
  { key: 'subCategory', labelKey: 'inventoryItems.columns.subCategory' },
]

export const InventoryItemsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const branchId = user?.selectedBranch?.branchId
  const [rows, setRows] = useState<ItemEntity[]>([])
  const [categories, setCategories] = useState<ItemLookupOption[]>([])
  const [subCategories, setSubCategories] = useState<ItemLookupOption[]>([])
  const [materialTypes, setMaterialTypes] = useState<ItemLookupOption[]>([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [totalCountFromApi, setTotalCountFromApi] = useState(0)
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
    code: true,
    barcode: true,
    name: true,
    description: true,
    type: true,
    category: true,
    subCategory: true,
  })

  useEffect(() => {
    if (!branchId) return
    const loadLookups = async () => {
      try {
        const [cats, subs, types] = await Promise.all([
          itemService.getCategories(branchId),
          itemService.getSubCategories(branchId),
          itemService.getMaterialTypesLookups(),
        ])
        setCategories(cats)
        setSubCategories(subs)
        setMaterialTypes(types)
      } catch {
        setCategories([])
        setSubCategories([])
        setMaterialTypes([])
      }
    }
    void loadLookups()
  }, [branchId])

  useEffect(() => {
    if (!branchId) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await itemService.getAllItems(branchId, pageNumber, PAGE_SIZE)
        setRows(data.items)
        setTotalCountFromApi(data.totalCount)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setRows([])
        setTotalCountFromApi(0)
        if (!isNoData) {
          toast.error(error.response?.data?.message || t('inventoryItems.failedToLoad'))
        }
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [branchId, pageNumber, t])

  const categoryById = useMemo(() => {
    const map: Record<number, string> = {}
    categories.forEach((c) => {
      map[c.id] = c.nameEn || c.nameAr
    })
    return map
  }, [categories])

  const subCategoryById = useMemo(() => {
    const map: Record<number, string> = {}
    subCategories.forEach((c) => {
      map[c.id] = c.nameEn || c.nameAr
    })
    return map
  }, [subCategories])

  const typeById = useMemo(() => {
    const map: Record<number, string> = {}
    materialTypes.forEach((x) => {
      map[x.id] = language === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr
    })
    return map
  }, [materialTypes, language])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return rows
    return rows.filter((r) =>
      [
        r.itemCode,
        r.barcode,
        r.itemNameEn,
        r.itemNameAr,
        r.reference,
        r.description,
        typeById[r.type] || String(r.type || ''),
        categoryById[r.itemCategoryId] || String(r.itemCategoryId || ''),
        subCategoryById[r.subCategoryId] || String(r.subCategoryId || ''),
      ].some((field) =>
        String(field).toLowerCase().includes(q)
      )
    )
  }, [rows, searchQuery, categoryById, subCategoryById, typeById])

  const totalCount = searchQuery.trim() ? filtered.length : totalCountFromApi
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const page = Math.min(pageNumber, totalPages)
  const pageSlice = searchQuery.trim() ? filtered.slice(0, PAGE_SIZE) : filtered

  const handleAdd = () => navigate('/inventory/items/new')

  const handleEdit = (row: ItemEntity) => {
    navigate(`/inventory/items/${row.itemId}/edit`)
  }

  const handleCopy = () => {
    if (filtered.length === 0) {
      toast.error(t('inventoryItems.nothingToCopy'))
      return
    }
    const header = ['Code', 'Barcode', 'Name', 'Description', 'Type', 'Category', 'Sub Category']
    const lines = filtered.map((r) =>
      [
        r.itemCode,
        r.barcode,
        r.itemNameEn,
        r.description,
        typeById[r.type] || '',
        categoryById[r.itemCategoryId] || '',
        subCategoryById[r.subCategoryId] || '',
      ].join('\t')
    )
    void navigator.clipboard.writeText([header.join('\t'), ...lines].join('\n'))
    toast.success(t('inventoryItems.copied'))
  }

  const handleExportCSV = () => {
    if (filtered.length === 0) {
      toast.error(t('inventoryItems.nothingToExport'))
      return
    }
    const header = 'Code,Barcode,Name,Description,Type,Category,Sub Category'
    const body = pageSlice
      .map((r) =>
        [
          r.itemCode,
          r.barcode,
          r.itemNameEn,
          r.description,
          typeById[r.type] || '',
          categoryById[r.itemCategoryId] || '',
          subCategoryById[r.subCategoryId] || '',
        ]
          .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n')
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `items_${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
    toast.success(t('inventoryItems.exported'))
  }

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const startIdx = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const endIdx = Math.min(page * PAGE_SIZE, totalCount)

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryItems.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryItems.listSubtitle')}</p>
          </div>
        </div>
        <Button onClick={handleAdd} className="w-full sm:w-auto shrink-0">
          <Plus className="mr-2 h-4 w-4" />
          {t('inventoryItems.addItem')}
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-sm font-medium">{t('inventoryItems.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('inventoryItems.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value)
                    setPageNumber(1)
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Columns className="mr-2 h-4 w-4" />
                    {t('inventoryItems.columnVisibility')}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>{t('inventoryItems.columnsToggle')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {ALL_COLUMNS.map((col) => (
                    <DropdownMenuCheckboxItem
                      key={col.key}
                      checked={visibleColumns[col.key]}
                      onCheckedChange={() => toggleColumn(col.key)}
                    >
                      {t(col.labelKey)}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <FileDown className="mr-2 h-4 w-4" />
                CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleCopy}>
                <Copy className="mr-2 h-4 w-4" />
                {t('inventoryItems.copy')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                  {visibleColumns.code && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryItems.columns.code')}
                    </TableHead>
                  )}
                  {visibleColumns.barcode && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryItems.columns.barcode')}
                    </TableHead>
                  )}
                  {visibleColumns.name && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryItems.columns.name')}
                    </TableHead>
                  )}
                  {visibleColumns.description && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryItems.columns.description')}
                    </TableHead>
                  )}
                  {visibleColumns.type && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryItems.columns.type')}
                    </TableHead>
                  )}
                  {visibleColumns.category && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryItems.columns.category')}
                    </TableHead>
                  )}
                  {visibleColumns.subCategory && (
                    <TableHead className="h-12 bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                      {t('inventoryItems.columns.subCategory')}
                    </TableHead>
                  )}
                  <TableHead className="h-12 w-[88px] bg-primary/5 px-4 text-center font-bold text-primary dark:bg-primary/10 dark:text-primary">
                    {t('common.edit')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageSlice.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={Object.values(visibleColumns).filter(Boolean).length + 1}
                      className="py-12 text-center text-muted-foreground"
                    >
                      {loading ? (
                        <div className="p-12 text-center">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                          <p className="mt-4 text-muted-foreground">{t('common.loading')}</p>
                        </div>
                      ) : (
                        t('inventoryItems.empty')
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  pageSlice.map((row) => (
                    <TableRow
                      key={row.itemId}
                      className="border-b transition-colors hover:bg-primary/5 dark:hover:bg-primary/10"
                    >
                      {visibleColumns.code && (
                        <TableCell className="px-4 py-2 text-center text-sm">{row.itemCode}</TableCell>
                      )}
                      {visibleColumns.barcode && (
                        <TableCell className="px-4 py-2 text-center text-sm">{row.barcode || '-'}</TableCell>
                      )}
                      {visibleColumns.name && (
                        <TableCell className="px-4 py-2 text-center text-sm font-medium">{row.itemNameEn}</TableCell>
                      )}
                      {visibleColumns.description && (
                        <TableCell className="px-4 py-2 text-center text-sm text-muted-foreground">
                          {row.description || '-'}
                        </TableCell>
                      )}
                      {visibleColumns.type && (
                        <TableCell className="px-4 py-2 text-center text-sm">
                          {typeById[row.type] || row.type || '-'}
                        </TableCell>
                      )}
                      {visibleColumns.category && (
                        <TableCell className="px-4 py-2 text-center text-sm">
                          {categoryById[row.itemCategoryId] || row.itemCategoryId || '-'}
                        </TableCell>
                      )}
                      {visibleColumns.subCategory && (
                        <TableCell className="px-4 py-2 text-center text-sm">
                          {subCategoryById[row.subCategoryId] || row.subCategoryId || '-'}
                        </TableCell>
                      )}
                      <TableCell className="px-4 py-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={() => handleEdit(row)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 border-t px-4 py-4 sm:flex-row sm:px-6">
            <p className="text-center text-sm text-muted-foreground sm:text-left">
              {t('inventoryItems.showing', { start: startIdx, end: endIdx, total: totalCount })}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-[2rem] text-center text-sm font-medium">{page}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
